// src/pages/JoinAccountPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { THEME_COLORS, THEMES } from '../components/Constants';

// Si vous utilisez votre service authFetch, vous avez besoin d'une fonction POST sans JWT.
// Nous allons l'implémenter directement ici ou dans un service public si nécessaire.
// Pour l'instant, on suppose une fonction utilitaire ou une route publique.

// 💡 Si vous n'avez pas de fonction d'API non sécurisée, ajoutez-la à `src/utils/api.js` :
// Ex: export const publicFetch = async (endpointPath, options = {}) => { ... };
// Pour cet exemple, nous allons utiliser une fonction interne `fetch` simple pour rester indépendant du JWT.
const BASE_API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";


export default function JoinAccountPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { notify } = useNotification();
    const { currentTheme } = useTheme();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

    const token = searchParams.get('token');
    
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isTokenValid, setIsTokenValid] = useState(!!token);

    useEffect(() => {
        if (!token) {
            notify("Lien d'invitation manquant ou invalide.", 'error');
            // Redirection après un court délai
            const timer = setTimeout(() => navigate('/'), 3000);
            return () => clearTimeout(timer);
        }
    }, [token, navigate, notify]);

    if (!isTokenValid) {
        return (
            <div style={styles.container(themeColors)}>
                <h2 style={{ color: themeColors.error }}>Lien Invalide</h2>
                <p style={{ color: themeColors.textSecondary }}>Redirection vers la page de connexion...</p>
            </div>
        );
    }
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!name || !password || !confirmPassword) {
            return notify("Veuillez remplir tous les champs.", 'warning');
        }

        if (password.length < 6) {
             return notify("Le mot de passe doit contenir au moins 6 caractères.", 'warning');
        }

        if (password !== confirmPassword) {
            return notify("Les mots de passe ne correspondent pas.", 'error');
        }

        setIsLoading(true);

        try {
            // ⭐ Appel à la nouvelle route API publique
            const response = await fetch(`${BASE_API_URL}/users/join-account`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, name, password }),
            });

            const data = await response.json();

            if (response.ok) {
                notify(data.message || "Compte configuré avec succès ! Connectez-vous.", 'success');
                navigate('/');
            } else {
                // Erreur serveur ou token expiré/invalide
                notify(data.error || "Échec de la configuration du compte. Le lien a peut-être expiré.", 'error');
                setIsTokenValid(false); // Pour déclencher le message d'erreur d'invalidation
            }
        } catch (error) {
            console.error("Erreur de connexion:", error);
            notify("Erreur de communication avec le serveur.", 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={styles.container(themeColors)}>
            <h2 style={{ color: themeColors.primary }}>Finaliser l'Inscription</h2>
            <p style={{ color: themeColors.textSecondary }}>
                Veuillez choisir un nom d'utilisateur et un mot de passe pour accéder au compte partagé.
            </p>
            <form onSubmit={handleSubmit} style={styles.form}>
                
                <label style={styles.label}>Nom d'utilisateur</label>
                <input
                    style={styles.input(themeColors)}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Votre nom ou pseudonyme"
                    required
                />
                
                <label style={styles.label}>Mot de passe</label>
                <input
                    style={styles.input(themeColors)}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 caractères"
                    required
                />

                <label style={styles.label}>Confirmer le mot de passe</label>
                <input
                    style={styles.input(themeColors)}
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                />
                
                <div style={{ gridColumn: '1 / span 2', marginTop: '20px' }}>
                    <button 
                        style={styles.submitButton(themeColors)} 
                        type="submit" 
                        disabled={isLoading}
                    >
                        {isLoading ? 'Configuration en cours...' : 'Créer mon compte et me connecter'}
                    </button>
                </div>
            </form>
        </div>
    );
}


const styles = {
    container: (colors) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '50px 20px',
        maxWidth: '500px',
        margin: '50px auto',
        backgroundColor: colors.mainBackground,
        borderRadius: '10px',
        boxShadow: `0 4px 12px ${colors.shadow}`,
    }),
    form: {
        display: 'grid',
        gridTemplateColumns: '150px 1fr',
        gap: '15px',
        width: '100%',
        maxWidth: '400px',
        marginTop: '20px',
    },
    label: {
        fontWeight: 'bold',
        alignSelf: 'center',
        color: 'inherit',
        textAlign: 'right',
    },
    input: (colors) => ({
        padding: '10px',
        borderRadius: '4px',
        border: `1px solid ${colors.inputBorder}`,
        backgroundColor: colors.inputBackground,
        color: colors.text,
        boxSizing: 'border-box',
    }),
    submitButton: (colors) => ({
        width: '100%',
        padding: '12px 20px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: colors.success,
        color: colors.textSecondary,
        cursor: 'pointer',
        fontWeight: 'bold',
        fontSize: '1em',
        transition: 'background-color 0.3s',
        ':hover': {
            backgroundColor: colors.primary,
        }
    }),
};